import { useState, useEffect } from 'react';

/**
 * Custom hook for managing localStorage with SSR safety and error handling
 * @param {string} key - localStorage key
 * @param {any} defaultValue - default value if key doesn't exist
 * @returns {[any, Function]} - tuple of [value, setValue]
 */
export function useLocalStorage(key, defaultValue) {
  // Lazy initialization with SSR safety check
  const [value, setValue] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Update localStorage when value changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Handle QuotaExceededError and other localStorage errors
      if (error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Consider clearing old data.');
      } else {
        console.error(`Error writing to localStorage key "${key}":`, error);
      }
    }
  }, [key, value]);

  return [value, setValue];
}
